import { useMsal } from "@azure/msal-react";
import { InteractionType, PopupRequest } from "@azure/msal-browser";
import { useEffect, useState } from "react";
import { useMicrosoftAuthCallback, useSignIn } from "../../queries/auth";
import { toast } from "react-toastify";
import { useGoogleAuthStore } from "../../store/useGoogleAuthStore";
import SessionStorageService, {
  SessionStorageKeys,
} from "../../utils/sessionStorage";
import { decodeAuthToken } from "../../utils/auth";
import { trackEvent } from "../../utils/AmpAnalytics";
import { PlatformEnum, PlatformUrlMapping } from "../../shared/utils";
import { useAuthStore } from "../../store/useAuthStore";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthStrategyType, UserType } from "../../types/auth";
import { WarningModal } from "./LoginComponents";
import { useFeatureFlag } from "../../FeatureFlags";

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID || "",
    authority: "https://login.microsoftonline.com/common",
    // redirectUri:
    //   process.env.REACT_APP_MICROSOFT_REDIRECT_URL ??
    //   "http://identity.dev.hissa.com", // enable this for local
  },
};

// openid/profile/email ensure the returned ID token carries the verified `email`
// claim the backend now requires (alongside the `xms_edov` optional claim that must
// be enabled on the Azure app registration). preferred_username is no longer trusted.
const loginRequest: PopupRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
};
export const MicrosoftLoginButton = () => {
  const { instance } = useMsal();
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  let platform: PlatformEnum | null | undefined = queryParams.get(
    "platform"
  ) as PlatformEnum;
  let redirect = platform ? PlatformUrlMapping[platform] : undefined;
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credentialResponse, setCredentialResponse] = useState<string>("");

  const {
    email: googleEmail,
    isLoading,
    clear: clearGoogleAuth,
    data: googleData,
    microsoftToken,
    accessToken: googleToken,
  } = useGoogleAuthStore();

  function ResetEmail() {
    sessionStorage.clear();
    useGoogleAuthStore.setState((store) => ({
      ...store,
      email: undefined,
      isLoading: false,
    }));
  }

  const { data, isError, error } = useMicrosoftAuthCallback(
    credentialResponse,
    enabled
  );

  const sessionService = new SessionStorageService();
  const isMarketplace = useFeatureFlag("allowUserRegistration");

  const { mutate: signIn } = useSignIn();
  function HandleSelectedRole() {
    const userInfo = sessionService.getItem(
      SessionStorageKeys.ALLOWED_ACCESS
    ) as any[];
    if (microsoftToken && microsoftToken !== "") {
      setLoading(true);
      signIn(
        {
          email: googleEmail || "",
          authStrategyType: AuthStrategyType.MICROSOFT,
          userType:
            userInfo[0].type === "EQUION" ? UserType.EQUION : UserType.EMPLOYEE,
          microsoftAuthCode: microsoftToken || "",
        },
        {
          onSuccess: (data) => {
            setLoading(false);
            setShowWarning(false);
            if (data.data) {
              const accessToken = data.data;
              const decodedJwt = decodeAuthToken(accessToken);
              trackEvent("Logging In with Google", {
                email: googleEmail,
                name: decodedJwt.fullName,
              });
              const userType =
                userInfo[0].type === "EQUION" ? "Admin" : "Employee";
              toast("Signin Successful Signed in as " + userType, {
                type: "success",
                autoClose: 2000,
              });
              if (isMarketplace) {
                const marketplaceLink = process.env.REACT_APP_MARKETPLACE_URL;
                window.location.href = marketplaceLink || "/login";
              } else if (redirect) {
                window.location.href = redirect;
              } else if (
                googleData.data !== undefined &&
                googleData?.data?.length > 0 &&
                redirect === undefined
              ) {
                const allowedType = googleData.data?.find(
                  (data: any) => data.type === userInfo[0].type
                );
                if (allowedType && allowedType?.lastUsedProduct) {
                  const lastUsedProductLink =
                    PlatformUrlMapping[
                      allowedType?.lastUsedProduct as PlatformEnum
                    ];
                  window.location.href =
                    lastUsedProductLink || "/landing/dashboard";
                } else {
                  setAuth({
                    accessToken,
                    isAuthenticated: true,
                    email: decodedJwt.emailId,
                    name: decodedJwt.fullName,
                    id: decodedJwt.id,
                  });
                  navigate("/landing/dashboard");
                }
              } else {
                setAuth({
                  accessToken,
                  isAuthenticated: true,
                  email: decodedJwt.emailId,
                  name: decodedJwt.fullName,
                  id: decodedJwt.id,
                });
                navigate("/landing/dashboard");
              }

              sessionService.removeItem(SessionStorageKeys.GOOGLE_TOKEN);
              sessionService.removeItem(SessionStorageKeys.ALLOWED_ACCESS);
              sessionService.removeItem(SessionStorageKeys.LOGIN_STEP);
              clearGoogleAuth();
            }
          },
          onError: (err: any) => {
            setLoading(false);
            setShowWarning(false);
            const msg =
              err.response.data.message ??
              "Something went wrong, try again later";
            toast(msg, {
              type: "error",
              autoClose: 2000,
            });
          },
        }
      );
    }
  }

  function HandleSubmit() {
    if (data?.data?.data && data.data?.type === "token") {
      const accessToken = data.data.data;
      const decodedJwt = decodeAuthToken(accessToken);
      trackEvent("Logging In with Microsoft", {
        email: decodedJwt.emailId,
        name: decodedJwt.fullName,
      });
      setEnabled(false);
      if (redirect) {
        window.location.href = redirect;
      } else if (data?.data?.userInfo[0]?.lastUsedProduct !== undefined) {
        const lastUsedProductLink =
          PlatformUrlMapping[
            data?.data?.userInfo[0]?.lastUsedProduct as PlatformEnum
          ];
        window.location.href = lastUsedProductLink || "/landing/dashboard";
      } else {
        setAuth({
          accessToken,
          isAuthenticated: true,
          email: decodedJwt.emailId,
          name: decodedJwt.fullName,
          id: decodedJwt.id,
        });
        navigate("/landing/dashboard");
      }
    }
    if (data?.data?.data && data.data?.type === "info") {
      useGoogleAuthStore.setState((store) => ({
        ...store,
        microsoftToken: credentialResponse,
        email: data.data?.data[0]?.email,
        data: data.data,
      }));
      if (data.data.data?.length === 1) {
        if (data?.data?.data[0]?.type === UserType.EMPLOYEE) {
          window.location.href =
            process.env.REACT_APP_EMPLOYEE_URL ||
            "https://employee.dev.hissa.com";
        } else if (
          data.data?.data[0].hasActiveSession === true &&
          data.data?.data[0].allowMultiAccess === false
        ) {
          setShowWarning(true);
        }
      } else sessionService.setItem(SessionStorageKeys.LOGIN_STEP, 2);
      sessionService.setItem(SessionStorageKeys.ALLOWED_ACCESS, data.data.data);
    }
  }

  useEffect(() => {
    if (data) HandleSubmit();
  }, [data]);

  useEffect(() => {
    if (isError) {
      toast((error as any).response?.data?.message, {
        type: "error",
        autoClose: 2000,
      });
      useGoogleAuthStore.setState((store) => ({ ...store, isLoading: false }));
      setEnabled(false);
    }
  }, [isError, error]);

  const handleMicrosoftLogin = () => {
    instance
      .loginPopup(loginRequest)
      .then((response) => {
        setEnabled(true);
        setCredentialResponse(response.idToken);
        useGoogleAuthStore.setState((store) => ({
          ...store,
          isLoading: true,
        }));
      })
      .catch((error) => {
        if (
          error.errorCode === "user_cancelled" ||
          error.errorCode === "popup_window_error" ||
          error.errorMessage?.includes("popup window closed")
        ) {
          useGoogleAuthStore.setState((store) => ({
            ...store,
            isLoading: false,
          }));
          console.log("Popup was closed by the user.");
        } else {
          console.error("Microsoft login error:", error);
        }
        setEnabled(false);
      });
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          useGoogleAuthStore.setState((store) => ({
            ...store,
            isLoading: true,
          }));
          handleMicrosoftLogin();
        }}
        className="w-[275px] flex items-center justify-center border border-gray-300 rounded px-4 py-2 hover:bg-blue-50 transition bg-white"
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
          alt="Microsoft logo"
          className="w-5 h-5 ml-3 mr-2 flex items-start"
        />
        <span className="text-gray-700 text-sm font-medium">
          Sign in with Microsoft
        </span>
      </button>
      {showWarning && (
        <WarningModal
          open={showWarning}
          onClose={() => {
            setShowWarning(false);
            ResetEmail();
          }}
          onSubmit={() => {
            setShowWarning(false);
            HandleSelectedRole();
          }}
          isLoading={loading}
        />
      )}
    </div>
  );
};
